import {useState, useEffect, useRef, useCallback} from 'react'
import {useToast} from '@/hooks/use-toast'
import {useI18n} from '@/i18n'

interface UseCopyToClipboardResult {
    copy: (text: string) => Promise<void>
    copied: boolean
    error: string | null
}

export function useCopyToClipboard(): UseCopyToClipboardResult {
    const [copied, setCopied] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const {toast} = useToast()
    const i18n = useI18n()
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current)
            }
        }
    }, [])

    const copy = useCallback(async (text: string): Promise<void> => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }

        try {
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(text)
            } else {
                const textarea = document.createElement('textarea')
                textarea.value = text
                // eslint-disable-next-line i18next/no-literal-string
                textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px'
                document.body.appendChild(textarea)
                textarea.focus()
                textarea.select()
                document.execCommand('copy')
                document.body.removeChild(textarea)
            }

            setError(null)
            setCopied(true)
            timerRef.current = setTimeout(() => {
                setCopied(false)
                timerRef.current = null
            }, 2000)
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err)
            setError(message)
            toast({variant: 'destructive', description: i18n.common.copyFailed as string})
        }
    }, [toast, i18n])

    return {copy, copied, error}
}
